import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { Button, Modal, Input, Select, Spinner, message } from '../../ui';
import { request } from '../../api/client';
import { inr2 } from '../../lib/format';
import { ClaimMoneyPicture, ClaimOutcomeSummary } from './ClaimMoneyPicture';

/**
 * SETTLE AN APPROVED CLAIM — record how the rep was paid and close it.
 *
 * One dialog for both places Accounts settles from: the claim page and the register's row action. It
 * loads the claim itself, because the figure that matters — what is still owed once the rep's advance
 * is used up — is computed by the SERVER on the detail read and is not on the list rows. The list
 * only knows the approved total, and paying that would pay the advance twice.
 */
export function PayExpenseReportModal({ reportId, onClose }: { reportId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [payMode, setPayMode] = useState<string | undefined>();
  const [payRef, setPayRef] = useState('');
  const [payBank, setPayBank] = useState<string | undefined>();

  const { data: report, isLoading, isError } = useQuery({
    queryKey: ['expense-report', reportId],
    queryFn: () => request<any>({ url: `/expense-reports/${reportId}` }),
    // The advance split moves whenever another claim of this rep settles — never pay off a stale one.
    refetchOnMount: 'always',
  });
  // Company accounts to pay from. ACTIVE accounts only (same options endpoint the payment screens use,
  // primary first): the ledger rejects an inactive or unknown account, so offering one would only
  // produce an error.
  const { data: bankAccounts } = useQuery({
    queryKey: ['bank-account-options'],
    queryFn: () => request<any[]>({ url: '/company/bank-accounts/options' }),
  });

  const settle = useConfirmedMutation(
    { title: 'Settle this claim?', content: 'The reimbursement is recorded as paid and the claim is closed.', okText: 'Yes, settle' },
    {
      mutationFn: (data: any) => request({ url: `/expense-reports/${reportId}/close`, method: 'PATCH', data }),
      onSuccess: () => {
        message.success(`${report?.reportNo ?? 'Claim'} settled`);
        qc.invalidateQueries({ queryKey: ['expense-report', reportId] });
        qc.invalidateQueries({ queryKey: ['expense-reports'] });
        onClose();
      },
      onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
    },
  );

  // Settlement comes from the SERVER — the same arithmetic it will commit — so the figure shown is the
  // figure that gets paid.
  const settlement = report?.settlement ?? null;
  // A claim spent out of collected retailer cash is never reimbursed — it only reduces what the rep
  // still owes. The server's split still reports that spend under `advanceConsumed`, so reading it as
  // "adjusted from advance" would name the wrong bag (same distinction the claim page draws).
  const cashFunded = String(report?.fundingSource ?? 'WALLET').toUpperCase() === 'CASH_CUSTODY';
  const due = cashFunded ? 0 : Number(settlement?.reimbursementDue ?? 0);
  // Someone else may have settled or reopened it between the list loading and this dialog opening.
  const stillApproved = report?.status === 'APPROVED';

  return (
    <Modal
      open
      width={760}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={!report ? 'Settle claim' : due > 0 ? `Pay reimbursement · ${report.reportNo}` : `Close as settled · ${report.reportNo}`}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary" loading={settle.isPending}
            disabled={!report || !stillApproved || (due > 0 && (!payMode || (payMode !== 'CASH' && !payBank)))}
            onClick={() => settle.mutate(
              // With nothing payable the server needs no mode or account, and posts no ledger row.
              due > 0 ? { paymentMode: payMode, paymentReference: payRef.trim() || undefined, bankAccountId: payMode !== 'CASH' ? payBank : undefined } : {},
            )}
          >
            {due > 0 ? `Pay reimbursement ${inr2(due)}` : 'Close as settled'}
          </Button>
        </>
      }
    >
      {isLoading ? (
        <div className="flex items-center justify-center gap-2 py-8 text-muted"><Spinner /> Loading the claim…</div>
      ) : isError || !report ? (
        <div className="rounded-card border border-line bg-surface-2 px-3 py-2.5 text-sm text-danger-600 dark:text-danger-500">
          Couldn’t load this claim, so what is owed can’t be worked out. Close this and try again.
        </div>
      ) : !stillApproved ? (
        <div className="rounded-card border border-line bg-surface-2 px-3 py-2.5 text-sm text-muted">
          {report.reportNo} is now {String(report.status ?? '').replace(/_/g, ' ').toLowerCase()} — there is nothing
          left to settle on it.
        </div>
      ) : (
        <div className="space-y-4">
          {/* The whole point of the screen: what was approved, what the advance already covers, and
              what is therefore actually payable. Server-supplied — the client never does this sum. */}
          {report.moneyPicture ? (
            /* The rep's whole position — float carried, older claims ahead, retailer cash in hand — and
               what this payment is, from the same figures the commit uses (see ClaimMoneyPicture). */
            <div className="space-y-2">
              <ClaimMoneyPicture picture={report.moneyPicture} mode="pay" />
              <ClaimOutcomeSummary
                verb="pay" picture={report.moneyPicture} repName={report.repName}
                amount={Number(report.paidAmount ?? report.subtotalAmount ?? 0)} fundingSource={report.fundingSource}
                cashPart={Number(report.cashFundedAmount ?? 0)}
              />
            </div>
          ) : (
          <div className="rounded-card border border-line bg-surface-2 px-3 py-2.5 text-sm">
            <div className="mb-2 font-semibold text-content">{report.repName ?? `#${report.repId}`}</div>
            <dl className="space-y-1">
              <div className="flex justify-between gap-4">
                <dt className="text-muted">Approved expenses</dt>
                <dd className="tabular-nums text-content">{inr2(settlement?.approved ?? 0)}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted">{cashFunded ? 'Spent from collected cash they hold' : 'Adjusted from advance'}</dt>
                <dd className="tabular-nums text-content">−{inr2(cashFunded ? (settlement?.approved ?? 0) : (settlement?.advanceConsumed ?? 0))}</dd>
              </div>
              <div className="flex justify-between gap-4 border-t border-line pt-1">
                <dt className="font-medium text-content">Amount to pay now</dt>
                <dd className="font-semibold tabular-nums text-content">{inr2(due)}</dd>
              </div>
              {due === 0 && (
                <div className="flex justify-between gap-4">
                  <dt className="text-muted">Paid from</dt>
                  <dd className="text-right text-content">Nothing leaves a company bank account</dd>
                </div>
              )}
              {!cashFunded && Number(settlement?.unusedAdvance ?? 0) > 0 && (
                <div className="flex justify-between gap-4">
                  <dt className="text-muted">Advance remaining with employee</dt>
                  <dd className="tabular-nums text-content">{inr2(settlement?.unusedAdvance)}</dd>
                </div>
              )}
            </dl>
          </div>
          )}

          {/* Nothing to pay: the picture's own outcome line already says why, so the note is the
              fallback for a claim whose picture could not be read. */}
          {due === 0 ? (!report.moneyPicture && (cashFunded ? (
            <div className="rounded-card border border-line bg-surface-2 px-3 py-2.5 text-sm text-muted">
              Paid out of retailer cash this person collected and had not yet banked, so nothing is
              reimbursed and no payment will be recorded — the approved amount already came off what they
              owe the company.
            </div>
          ) : (
            <div className="rounded-card border border-line bg-surface-2 px-3 py-2.5 text-sm text-muted">
              Their company float already covers this claim, so there is nothing to pay — no payment mode
              or bank account is needed, and no payment will be recorded. The payment details are asked
              only when the claim costs more than the float they hold.{Number(settlement?.unusedAdvance ?? 0) > 0
                ? ` ${inr2(settlement?.unusedAdvance)} stays with the employee as float until it is returned separately.`
                : ''}
            </div>
          ))) : (
          <div>
            <label className="mb-1 block text-sm text-muted">How they were paid <span className="text-danger-500">*</span></label>
            <Select
              className="w-full" placeholder="Payment mode" value={payMode} onChange={setPayMode}
              options={['CASH', 'UPI', 'BANK_TRANSFER', 'CHEQUE', 'CARD', 'OTHER'].map((m) => ({ value: m, label: m.replace(/_/g, ' ') }))}
            />
          </div>
          )}
          {/* Anything other than cash left a company account, and the ledger refuses to record it
              without naming which — otherwise it can never be reconciled against a statement. */}
          {due > 0 && payMode && payMode !== 'CASH' && (
            <div>
              <label className="mb-1 block text-sm text-muted">Paid from <span className="text-danger-500">*</span></label>
              <Select
                className="w-full" placeholder="Company bank account" value={payBank} onChange={setPayBank}
                options={(bankAccounts ?? []).map((a: any) => ({
                  value: String(a.id),
                  label: `${a.label || a.bankName}${a.last4 ? ` · ····${a.last4}` : ''}`,
                }))}
                notFoundContent="No active company bank account — add or reactivate one in Company & Billing setup"
              />
            </div>
          )}
          {due > 0 && (
          <div>
            <label className="mb-1 block text-sm text-muted">Reference <span className="text-subtle">(optional)</span></label>
            <Input value={payRef} onChange={(e) => setPayRef(e.target.value)} placeholder="UTR / cheque no. / voucher" />
          </div>
          )}
        </div>
      )}
    </Modal>
  );
}
